import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import { mapRolesFromDTO } from '../mappers/role.mapper';
import {
  DeleteRoleRequest,
  UpsertRoleRequestItem,
  UpsertRolesRequest,
} from '../types/request/role-request';
import { RoleWithMemberCountResponse } from '../types/response/role-with-member-count-response';
import { Role } from '../types/role';

/**
 * Fetches all roles for the organization
 * Returns complete list in single request (no pagination)
 */
export const getRoles = async (): Promise<Role[]> => {
  const endpoint = '/api/employee-roles';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: RoleWithMemberCountResponse[] }>(endpoint)
  );

  return mapRolesFromDTO(response);
};

/**
 * Updates roles in bulk (create new, update existing)
 * Sends only roles with valid names
 */
export const updateRoles = async (roles: UpsertRoleRequestItem[]): Promise<Role[]> => {
  const endpoint = '/api/employee-roles';

  // Filter out new roles (negative IDs) and include only those with names
  const rolesToUpdate = roles
    .filter(role => role.id && role.id > 0)
    .map(role => ({
      id: role.id,
      name: role.name,
    }));

  // Include new roles (without ID)
  const newRoles = roles
    .filter(role => !role.id || role.id < 0)
    .map(role => ({
      name: role.name,
    }));

  const requestBody: UpsertRolesRequest = {
    roles: [...rolesToUpdate, ...newRoles],
  };

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.patch<{ data: RoleWithMemberCountResponse[] }>(endpoint, requestBody)
  );

  return mapRolesFromDTO(response);
};

/**
 * Deletes a role and optionally transfers members to another role
 */
export const deleteRole = async ({
  roleId,
  replacementRoleId,
}: {
  roleId: number;
  replacementRoleId?: number;
}): Promise<void> => {
  const endpoint = '/api/employee-roles';

  const requestBody: DeleteRoleRequest = {
    employeeRoleId: roleId,
    ...(replacementRoleId !== undefined && { replacementRoleId }),
  };

  await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.delete(endpoint, { data: requestBody })
  );
};
